import { useAppDispatch } from '../../stores/redux.store';
import { resetAppState, setGlobalStateLoading } from '../../actions/app-actions';
import { useLocation } from 'react-router-dom';
import { buildInitialState, PreloadedAppState } from '../init-app-state';
import { useEffect } from 'react';
import hasNoInjectedServerState from '../has-no-injected-server-state';
import { fetchApi, fetchApiWithRetry, ServerErrorException } from '../fetch-api';
import { captureErrorMessage, captureTaggedException } from '../analytics/error-reporting';
import { setServerError } from '../../actions/server-error-actions';
import { ServerError } from '../../types';
import { checkFeatureGateOnceInitialized } from '../feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';
import { useNavigateSoftOrHard } from './useNavigateSoftOrHard';
import { Capability } from '../analytics/capability-tags';

const SAS_FAST_FIVE_FETCH_HEADER = 'sas-fast-five-fetch';
const EXCLUDED_PATHS = {
  previousReduxStore: [
    '/2step/enrollment/',
    '/signup/welcome/p/loom-create-workspace',
    '/aws-captcha-test',
  ],
} as const;

/**
 * This hook is used on each new page load to fetch the initial state of the app from the
 * server and refresh the full redux state with that data.
 */
export const useGlobalPageState = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();

  useEffect(() => {
    const fetchData = async () => {
      if (await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS)) {
        return;
      }

      // For non-bifrost pages (i.e. injected state from sign-in-service), use the existing non-sas
      // behaviour. This could mean we may have old injected state which doesn't get
      // refreshed from here on page navigation, but that matches existing behaviour
      const hasInjectedServerState = !hasNoInjectedServerState();
      if (hasInjectedServerState) {
        return;
      }

      const requiresStateFromPreviousReduxStore = EXCLUDED_PATHS.previousReduxStore.some(path =>
        location.pathname.startsWith(path)
      );
      if (requiresStateFromPreviousReduxStore) {
        return;
      }

      dispatch(setGlobalStateLoading(true));

      try {
        const url = location.pathname + location.search;
        const data = await fetchFast5GlobalPageState(url);

        if (isRedirect(data)) {
          navigate(data.redirectTo);
          return;
        }

        const fullState = await buildInitialState(data);
        dispatch(resetAppState(fullState.initialStoreState));
      } catch (error) {
        let errorType: ServerError = { type: 'internal_server_error' };
        if (error instanceof ServerErrorException) {
          errorType = error.errorType;
          captureErrorMessage(`Failed to load AppState in Bifrost: ${error.message}`);
        } else {
          captureErrorMessage(`Unexpected error while loading AppState in Bifrost: ${error}`);
        }
        dispatch(setServerError(errorType));
      }

      dispatch(setGlobalStateLoading(false));
    };

    fetchData();

    // re-fetch whenever we change page, i.e. the fixed route
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
};

export type ServerSideRedirect = { redirectTo: string };

export const isRedirect = (
  data: PreloadedAppState | ServerSideRedirect | null
): data is ServerSideRedirect => Boolean(data && 'redirectTo' in data);

export const fetchFast5GlobalPageState = async (
  fullPageUrl: string
): Promise<PreloadedAppState | ServerSideRedirect | null> => {
  const headers = {
    [SAS_FAST_FIVE_FETCH_HEADER]: 'true',
  };
  const response = await fetchApi(fullPageUrl, headers);
  const data = await response.json();

  return data;
};

export const fetchPageState = async (
  restStateEndpoint: string,
  request: Request,
  tags: {
    capabilities: Capability[];
  }
): Promise<PreloadedAppState | ServerSideRedirect | null> => {
  const url = new URL(request.url);

  const response = await fetchApiWithRetry(restStateEndpoint + url.search);

  if (response.status === 404 || response.status >= 500) {
    captureTaggedException('Error fetching page state', {
      capabilities: tags.capabilities,
      extraProperties: {
        restStateEndpoint,
        responseStatus: response.status,
      },
    });
  }

  try {
    return await response.json();
  } catch (parseError) {
    captureErrorMessage(`Unexpected error while parsing AppState in Bifrost: ${parseError}`);
    return null;
  }
};
